import type { VectorEdge, VectorNetwork } from "@/document/model/vectorNetwork";

export function translateConnectedHandles(network: VectorNetwork, vertexId: string, dx: number, dy: number): void {
  if (dx === 0 && dy === 0) {
    return;
  }

  for (const edge of network.edges) {
    if (edge.from.vertexId === vertexId && edge.from.handle) {
      edge.from.handle.x += dx;
      edge.from.handle.y += dy;
    }

    if (edge.to.vertexId === vertexId && edge.to.handle) {
      edge.to.handle.x += dx;
      edge.to.handle.y += dy;
    }
  }
}

export function mergeVectorVertices(
  network: VectorNetwork,
  sourceVertexId: string,
  targetVertexId: string,
  sourceDrag?: { from: { x: number; y: number }; to: { x: number; y: number } },
): void {
  if (sourceVertexId === targetVertexId) {
    return;
  }

  const sourceExists = network.vertices.some(vertex => vertex.id === sourceVertexId);
  const targetExists = network.vertices.some(vertex => vertex.id === targetVertexId);
  if (!sourceExists || !targetExists) {
    throw new Error(`Cannot merge vertices ${sourceVertexId} and ${targetVertexId}.`);
  }

  if (sourceDrag) {
    translateConnectedHandles(
      network,
      sourceVertexId,
      sourceDrag.to.x - sourceDrag.from.x,
      sourceDrag.to.y - sourceDrag.from.y,
    );
  }

  for (const edge of network.edges) {
    if (edge.from.vertexId === sourceVertexId) {
      edge.from.vertexId = targetVertexId;
    }

    if (edge.to.vertexId === sourceVertexId) {
      edge.to.vertexId = targetVertexId;
    }
  }

  network.edges = dedupeUndirectedEdges(network.edges.filter(edge => edge.from.vertexId !== edge.to.vertexId));
  network.vertices = network.vertices.filter(vertex => vertex.id !== sourceVertexId);
}

function dedupeUndirectedEdges(edges: VectorEdge[]): VectorEdge[] {
  const seen = new Set<string>();
  const deduped: VectorEdge[] = [];

  for (const edge of edges) {
    const key = undirectedEdgeKey(edge.from.vertexId, edge.to.vertexId);
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    deduped.push(edge);
  }

  return deduped;
}

function undirectedEdgeKey(fromVertexId: string, toVertexId: string): string {
  return fromVertexId < toVertexId ? `${fromVertexId}|${toVertexId}` : `${toVertexId}|${fromVertexId}`;
}
